import * as React from 'react';
import { Sun, Moon } from 'lucide-react';
import { IconButton } from './icon-button';
import { useTheme } from '../../hooks/useTheme';
import { cn } from '../../lib/utils';

/**
 * ThemeToggle - Button to toggle between light and dark themes
 *
 * Displays a sun icon in dark mode and moon icon in light mode.
 * Uses the useTheme hook for theme management.
 *
 * @example
 * // Basic usage
 * <ThemeToggle />
 *
 * // With custom size
 * <ThemeToggle size="lg" />
 *
 * // With custom variant
 * <ThemeToggle variant="ghost" />
 */
const ThemeToggle = React.forwardRef(
  ({ className, variant = 'ghost', size = 'md', ...props }, ref) => {
    const { resolvedTheme, toggle } = useTheme();

    return (
      <IconButton
        ref={ref}
        variant={variant}
        size={size}
        className={cn('relative', className)}
        onClick={toggle}
        aria-label={`Switch to ${resolvedTheme === 'light' ? 'dark' : 'light'} mode`}
        title={`Switch to ${resolvedTheme === 'light' ? 'dark' : 'light'} mode`}
        {...props}
      >
        {/* Sun icon - visible in dark mode */}
        <Sun
          className={cn(
            'h-4 w-4 transition-all duration-200',
            resolvedTheme === 'dark'
              ? 'rotate-0 scale-100 opacity-100'
              : 'rotate-90 scale-0 opacity-0 absolute'
          )}
        />
        {/* Moon icon - visible in light mode */}
        <Moon
          className={cn(
            'h-4 w-4 transition-all duration-200',
            resolvedTheme === 'light'
              ? 'rotate-0 scale-100 opacity-100'
              : '-rotate-90 scale-0 opacity-0 absolute'
          )}
        />
      </IconButton>
    );
  }
);

ThemeToggle.displayName = 'ThemeToggle';

export { ThemeToggle };
